import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, hasAuthParams } from "react-oidc-context";
import Navigation from "./Navigation";
import NoAccess from "./NoAccess";
import { AccessError, CheckAccess, TrackVisit } from "../services/ApiService";

const ACCESS_CACHE_KEY = "im_access";

function readCache() {
  try {
    const raw = sessionStorage.getItem(ACCESS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify(data));
  } catch {}
}

function MainLayout(props) {
  const auth = useAuth();
  const { children } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);

  const cached = readCache();
  const [accessStatus, setAccessStatus] = useState(cached ? "granted" : "unknown");
  const [isAdmin, setIsAdmin] = useState(cached?.is_admin === true);

  useEffect(() => {
    if (!hasAuthParams() &&
      !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
    return auth.events.addUserLoaded(() => {
      // Only redirect off the OIDC callback route. A background silent
      // token renewal also fires this event, and would otherwise yank
      // the user back to /ytd_report from whatever page they're on.
      if (location.pathname !== "/loginCallback") return;
      navigate("/ytd_report");
    })
  }, [auth, hasTriedSignin, navigate, location.pathname]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) return;
    if (accessStatus !== "unknown") return;

    CheckAccess(auth.user.id_token)
      .then((data) => {
        const admin = data?.is_admin === true;
        writeCache({ is_admin: admin });
        setIsAdmin(admin);
        setAccessStatus("granted");
        TrackVisit(auth.user.id_token).catch(() => {});
      })
      .catch((err) => {
        if (err instanceof AccessError) {
          setAccessStatus("denied");
        } else {
          setAccessStatus("granted");
        }
      });
  }, [auth.isAuthenticated, auth.user, accessStatus]);

  if (!auth.isAuthenticated) {
    return <div>Redirecting to TTD Desk for signing in ...</div>;
  }

  if (accessStatus === "denied") {
    return <NoAccess />;
  }

  if (accessStatus === "unknown") {
    return null;
  }

  return (
    <>
      <header>
        <Navigation User={auth?.user.profile} isAdmin={isAdmin} onLogout={() => {
          sessionStorage.removeItem(ACCESS_CACHE_KEY);
          auth.signoutRedirect();
        }} />
      </header>
      <main className="container-fluid p-b-20">
        {children}
      </main>
    </>
  )
}

export default MainLayout;
